import { getPercent, warn } from '../../../utils/commonUtils';
import { PlayableTitle } from '../titles/types/title';
import { useBookmarks, useContinueWatchingTitles } from './bookmarks/hooks';

interface UseProgressOptions extends Pick<PlayableTitle, 'masterId' | 'position' | 'duration'> {
  playbackOptions?: PlayableTitle['playbackOptions'];
}

export function useProgress(options: UseProgressOptions) {
  const {
    data: bookmark,
    isLoading: isBookmarkLoading,
    isIdle: isBookmarkIdle,
  } = useBookmarks({
    select: (data) =>
      data.find((b) => {
        return (
          b.masterId === options.masterId ||
          options.playbackOptions?.some((option) => option.platformTitleId === b.platformTitleId)
        );
      }),
    enabled: options.position === undefined,
  });

  const {
    data: title,
    isLoading: isTitleLoading,
    isIdle: isTitleIdle,
  } = useContinueWatchingTitles({
    select: (data) =>
      data.find((t) => {
        return t.masterId === options.masterId;
      }),
    enabled: options.duration === null || options.duration === undefined,
  });

  const isLoading = isBookmarkLoading || isTitleLoading;
  const isIdle = isBookmarkIdle && isTitleIdle;

  if (isLoading) {
    return undefined;
  }

  if (isIdle) {
    return options.position !== undefined && options.duration !== undefined && options.duration !== null
      ? getPercent(options.position, options.duration)
      : undefined;
  }

  // Titles from /me/bookmarks/continuewatching already have a position
  const position = options.position ?? title?.position ?? bookmark?.position;
  const duration = options.duration ?? title?.duration;

  if (position === undefined) {
    warn('Cannot calculate progress without a position');
    return undefined;
  }

  if (duration === undefined || duration === null) {
    warn('Cannot calculate progress without a duration');
    return undefined;
  }

  if (position === 0 || duration === 0) {
    return undefined;
  }

  return bookmark !== undefined && bookmark.isWatched ? 100 : getPercent(position, duration);
}
